<template>
  <ModalWrapper :is-cancellable="false">
    <div class="p-32 pt-48">
      <div v-if="uiStore.sellerDialogSettings.clubInactive" class="text-lg mb-16">
        {{ $lang('seller', 'sales_closed') }}
      </div>
      <div v-if="uiStore.sellerDialogSettings.clubPastDeadline" class="text-lg mb-16">
        {{ $lang('seller', 'sales_past_deadline') }}
      </div>
      <div v-if="uiStore.sellerDialogSettings.sellerError || uiStore.sellerDialogSettings.sellerMissing">
        <div class="text-lg mb-16">
          <span v-if="uiStore.sellerDialogSettings.forceSellerSelection">
            {{ $lang('seller', 'choose_seller') }}
          </span>
          <span v-else-if="uiStore.sellerDialogSettings.sellerError">
            {{ $lang('seller', 'seller_not_found') }}
          </span>
          <span v-else>
            {{ $lang('seller', 'enter_seller_number') }}
          </span>
        </div>
        <div>
          <TextInput
            :placeholder="$lang('seller', 'enter_seller_number')"
            has-search-icon
            type="number"
            :has-error="error"
            :is-loading="loading"
            @submit-input="(e) => sellerNo = e"
          />
        </div>
      </div>

      <div v-if="foundInactiveClub" class="mt-16">
        {{ $lang('seller', 'club_inactive') }}
      </div>
      <div v-if="foundSeller" class="mt-16">
        {{ $lang('seller', 'selected_seller', {
          'seller': sellerCookie?.Name || '',
          'club': clubCookie?.Name || '',
        })
        }}
      </div>
    </div>
    <div class="flex justify-between">
      <button
        v-if="isCancellable"
        class="basis-1/2 btn btn--secondary btn--lg rounded-none"
        :class="{
          'basis-1/2': isCancellable,
          'basis-full': !showOkButton
        }"
        @click="clickCancel"
      >
        {{ $lang('seller', 'cancel') }}
      </button>
      <button
        v-if="showOkButton"
        class="btn btn--primary btn--lg rounded-none"
        :class="{
          'basis-1/2': isCancellable,
          'basis-full': !isCancellable
        }"
        :disabled="!okClickable"
        @click="clickOk"
      >
        {{ $lang('seller', 'ok') }}
      </button>
    </div>
  </ModalWrapper>
</template>

<script setup lang="ts">
import ModalWrapper from '~/components/global/ModalWrapper.vue';
import TextInput from '~/components/forms/TextInput.vue';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';

import { useUiStore } from '~/store/ui';
import { useConfigStore } from "~/store/config";
const { algoliaClubSearch, algoliaSellerSearch } = useAlgoliaSearch();

const uiStore = useUiStore();
const config = useConfigStore();

const isCancellable = computed(()=> {
  if (uiStore.sellerDialogSettings.forceSellerSelection) {
    return foundInactiveClub.value;
  }
  return (uiStore.sellerDialogSettings.sellerMissing || uiStore.sellerDialogSettings.sellerError);
});

const clubCookie = useCookie<SellerClub | null>('club', { maxAge: 3600 * 24 * 182, path: '/' });
const sellerCookie = useCookie<SellerPerson | null>('seller', { maxAge: 3600 * 24 * 182, path: '/' });
const isSeller = useCookie<boolean>('isSeller', { maxAge: 3600 * 24 * 182, path: '/' });

const foundInactiveClub = ref(false);

const okClickable =computed(()=> {
  if (foundSeller.value) {
    return true;
  }
  if (uiStore.sellerDialogSettings.clubInactive) {
    return true;
  }
  if (uiStore.sellerDialogSettings.clubPastDeadline) {
    return true;
  }
  return false;
});

const showOkButton = computed(()=> {
  if (foundInactiveClub.value) {
    return false;
  }
  return true;
  //return (uiStore.sellerDialogSettings.clubInactive);
});

const clickOk = () => {
  if (uiStore.sellerDialogSettings.clubInactive) {
    clubCookie.value = null;
    sellerCookie.value = null;
    isSeller.value = false;
    setTimeout(()=> {
      window.location.href = '/se';
    },250);
  } else {
    close();
  }
};

const clickCancel = () => {
  clubCookie.value = null;
  sellerCookie.value = null;

  if (foundInactiveClub.value) {
    isSeller.value = false;
  }
  close();
};

const sellerNo = ref('');
const foundSeller = ref(false);
const error = ref(false);
watch(sellerNo, ()=> {
  foundSeller.value = false;
  error.value = false;
  foundInactiveClub.value = false;
  doSearch();
});

const loading = ref(false);
const doSearch = async() => {
  if (sellerNo.value.length >= 8) {
    loading.value = true;
    const sellerRes = await algoliaSellerSearch(sellerNo.value);
    if (sellerRes && sellerRes.length === 1) {
      const clubRes = await algoliaClubSearch(sellerRes[0].RoundID);

      if (clubRes) {
        if (clubRes.length !== 1 || !clubRes[0].Active) {
          if (clubRes.length !== 1) {
            console.log('Wrong number clubs: ' + clubRes.length);
            error.value = true;
          } else {
            console.log('Inactive club');
            foundInactiveClub.value = true;
            clubCookie.value = null;
            sellerCookie.value = null;
          }
        } else {
          // We have both our seller and our club
          config.clubCookie = clubRes[0];
          config.sellerCookie = sellerRes[0];
          clubCookie.value = clubRes[0];
          sellerCookie.value = sellerRes[0];
          foundSeller.value = true;
          isSeller.value = true;
        }
      } else {
        console.log('Club error');
        error.value = true;
      }
    } else {
      error.value = true;
    }
  }
  loading.value = false;
};

const close = () => {
  uiStore.sellerDialogSettings = {};
  uiStore.showSellerDialog = false;
};

</script>

<style scoped lang="postcss">
button:disabled {
  opacity: 0.4;
}
</style>
